import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Zap, Loader2, CheckCircle2, XCircle, AlertCircle, Calendar, MapPin, DollarSign, RotateCcw, Download,
} from 'lucide-react'

/**
 * Vendor assignment accept/decline page.
 *
 * URL: /vendor/assignment/accept?token=...
 *      /vendor/assignment/decline?token=...
 *
 * Unauthenticated — token is the credential. Mirrors AcceptInvite's pattern:
 * raw fetch (NOT supabase.functions.invoke) to vendor-respond which is
 * deployed --no-verify-jwt. The apikey header is still required by the
 * Supabase Functions gateway to identify the project (anon key only).
 */

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendor-respond`
const WORK_ORDER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendor-work-order`
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// ─── Response shapes (mirrors supabase/functions/vendor-respond/index.ts) ───

type LookupState = 'ready' | 'already_responded' | 'cancelled' | 'expired'
type AssignmentStatus = 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled'

interface LookupBody {
  state: LookupState
  action: 'accept' | 'decline'
  status: AssignmentStatus
  responded_at: string | null
  expires_at: string
  assignment: {
    id: string
    job_title: string | null
    job_scheduled_date: string | null
    job_scheduled_start: string | null
    job_scheduled_end: string | null
    job_address: {
      line1: string | null; line2: string | null; city: string | null
      state: string | null; zip: string | null
    }
    // job_notes is the operator-authored "Job details" (work scope) field;
    // internal_notes is intentionally NOT in this payload — see edge fn
    // ASSIGNMENT_SELECT comment.
    job_notes: string | null
    work_order_number: string | null
    purchase_order_number: string | null
    vendor_name: string
    workspace_name: string
    // numeric arrives as string from PostgREST but JS clients occasionally
    // see it as number; widen so display + comparison are safe either way.
    budget_offered: string | number | null
    assigned_at: string
    // Server-signed URLs from createSignedUrls with a 1-hour TTL — see
    // PHOTO_SIGNED_URL_TTL_SECONDS in the edge function. Empty array when
    // the job has no photos.
    photos: { signedUrl: string; caption: string | null }[]
  }
}

// 410 raced body (vendor accepted on another device / op cancelled mid-flow).
interface RacedBody {
  state: LookupState
  status: AssignmentStatus
  responded_at: string | null
  expires_at: string
}

// ─── Local state machine ────────────────────────────────────────────────────

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ready'; lookup: LookupBody }
  | { kind: 'submitting'; lookup: LookupBody; inlineError: string }
  | { kind: 'done'; outcome: 'accepted' | 'declined'; workspaceName: string }
  | { kind: 'already_responded'; status: AssignmentStatus; respondedAt: string | null; workspaceName?: string }
  | { kind: 'expired'; expiresAt: string; workspaceName?: string }
  | { kind: 'cancelled'; workspaceName?: string }
  | { kind: 'network_error'; message: string }

interface Props { action: 'accept' | 'decline' }

export default function AssignmentRespond({ action }: Props) {
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [state, setState] = useState<PageState>({ kind: 'loading' })
  const [declineReason, setDeclineReason] = useState('')

  // useRef sequence token — safer than the closure-cancelled-flag pattern
  // per CLAUDE.md (React 18 microtask-race). Bump synchronously at the top
  // of every effect run; async resolutions re-check before applying state.
  const seqRef = useRef(0)

  const doLookup = () => {
    const mySeq = ++seqRef.current
    if (!token) {
      setState({ kind: 'invalid', message: 'No token in the URL. The link may be incomplete — try copy-pasting the full URL from the email.' })
      return
    }
    setState({ kind: 'loading' })
    ;(async () => {
      try {
        const res = await fetch(FUNCTIONS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
          body: JSON.stringify({ op: 'lookup', token }),
        })
        const json = await res.json().catch(() => ({}))
        if (mySeq !== seqRef.current) return

        if (res.status === 404) {
          setState({ kind: 'invalid', message: 'We couldn’t find this assignment. The link may be incomplete or the assignment may have been removed.' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'network_error', message: (json as { error?: string }).error || `Lookup failed (${res.status})` })
          return
        }
        const body = json as LookupBody
        applyLookupBody(body)
      } catch (e: unknown) {
        if (mySeq !== seqRef.current) return
        setState({ kind: 'network_error', message: e instanceof Error ? e.message : 'Network error' })
      }
    })()
  }

  const applyLookupBody = (body: LookupBody) => {
    const ws = body.assignment.workspace_name
    switch (body.state) {
      case 'ready':                setState({ kind: 'ready', lookup: body }); break
      case 'already_responded':    setState({ kind: 'already_responded', status: body.status, respondedAt: body.responded_at, workspaceName: ws }); break
      case 'expired':              setState({ kind: 'expired', expiresAt: body.expires_at, workspaceName: ws }); break
      case 'cancelled':            setState({ kind: 'cancelled', workspaceName: ws }); break
    }
  }

  useEffect(() => { doLookup() }, [token])  // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    if (state.kind !== 'ready') return
    const lookup = state.lookup
    setState({ kind: 'submitting', lookup, inlineError: '' })

    const reason = action === 'decline' && declineReason.trim() ? declineReason.trim() : undefined
    const mySeq = ++seqRef.current
    try {
      const res = await fetch(FUNCTIONS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
        body: JSON.stringify({ op: action, token, ...(reason ? { declineReason: reason } : {}) }),
      })
      const json = await res.json().catch(() => ({}))
      if (mySeq !== seqRef.current) return

      if (res.status === 200) {
        setState({ kind: 'done', outcome: action === 'accept' ? 'accepted' : 'declined', workspaceName: lookup.assignment.workspace_name })
        return
      }
      if (res.status === 410) {
        const body = json as RacedBody
        const ws = lookup.assignment.workspace_name
        switch (body.state) {
          case 'already_responded': setState({ kind: 'already_responded', status: body.status, respondedAt: body.responded_at, workspaceName: ws }); return
          case 'expired':           setState({ kind: 'expired', expiresAt: body.expires_at, workspaceName: ws }); return
          case 'cancelled':         setState({ kind: 'cancelled', workspaceName: ws }); return
          default:                  setState({ kind: 'network_error', message: 'This assignment is no longer available.' }); return
        }
      }
      if (res.status === 404) {
        setState({ kind: 'invalid', message: 'This link is no longer valid.' })
        return
      }
      const msg = (json as { error?: string }).error || `Request failed (${res.status})`
      setState({ kind: 'submitting', lookup, inlineError: msg })
    } catch (e: unknown) {
      if (mySeq !== seqRef.current) return
      const msg = e instanceof Error ? e.message : 'Network error'
      setState({ kind: 'submitting', lookup, inlineError: msg })
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', right: '25%', width: 500, height: 500, background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={{ width: '100%', maxWidth: 460, position: 'relative' }}>
        <BrandHeader />
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 32 }}>
          {renderBody(state, action, declineReason, setDeclineReason, handleConfirm, doLookup, token)}
        </div>
      </motion.div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function BrandHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--logo-glow)' }}>
        <Zap style={{ width: 18, height: 18, color: 'var(--accent-ink)' }} />
      </div>
      <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Orkestra</span>
    </div>
  )
}

function renderBody(
  state: PageState,
  action: 'accept' | 'decline',
  declineReason: string,
  setDeclineReason: (s: string) => void,
  handleConfirm: () => void,
  retry: () => void,
  token: string,
) {
  if (state.kind === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '20px 0' }}>
        <Loader2 style={{ width: 22, height: 22, color: 'var(--ink-tertiary)' }} className="animate-spin" />
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Looking up your assignment…</p>
      </div>
    )
  }

  if (state.kind === 'invalid') {
    return (
      <TerminalCopy
        icon={<AlertCircle style={{ width: 28, height: 28, color: 'var(--ink-tertiary)' }} />}
        title="Link not recognised"
        body={state.message}
      />
    )
  }

  if (state.kind === 'network_error') {
    return (
      <TerminalCopy
        icon={<AlertCircle style={{ width: 28, height: 28, color: 'var(--danger)' }} />}
        title="Something went wrong"
        body={state.message}
        cta={
          <button onClick={retry} className="btn-primary" style={{ justifyContent: 'center', marginTop: 18 }}>
            <RotateCcw style={{ width: 14, height: 14 }} /> Try again
          </button>
        }
      />
    )
  }

  if (state.kind === 'cancelled') {
    return (
      <TerminalCopy
        icon={<XCircle style={{ width: 28, height: 28, color: 'var(--ink-tertiary)' }} />}
        title="Assignment cancelled"
        body={`This assignment was cancelled${state.workspaceName ? ` by ${state.workspaceName}` : ''}. No action is needed from your side.`}
      />
    )
  }

  if (state.kind === 'expired') {
    return (
      <TerminalCopy
        icon={<AlertCircle style={{ width: 28, height: 28, color: 'var(--ink-tertiary)' }} />}
        title="Link expired"
        body={`This link expired on ${formatDate(state.expiresAt)}.${state.workspaceName ? ` Please contact ${state.workspaceName} for a fresh assignment.` : ''}`}
      />
    )
  }

  if (state.kind === 'already_responded') {
    const verb = state.status === 'accepted' ? 'accepted' : 'declined'
    const when = state.respondedAt ? ` on ${formatDate(state.respondedAt)}` : ''
    return (
      <TerminalCopy
        icon={state.status === 'accepted'
          ? <CheckCircle2 style={{ width: 28, height: 28, color: 'var(--positive)' }} />
          : <XCircle style={{ width: 28, height: 28, color: 'var(--ink-tertiary)' }} />}
        title={`Already ${verb}`}
        body={`This assignment was already ${verb}${when}.${state.workspaceName ? ` ${state.workspaceName} has the response on file.` : ''}`}
        // Accepted revisits keep download access; declined revisits don't
        // (the response was a "no thanks" — no work to document for them).
        cta={state.status === 'accepted' ? <DownloadWorkOrderButton token={token} /> : undefined}
      />
    )
  }

  if (state.kind === 'done') {
    const isAccept = state.outcome === 'accepted'
    return (
      <TerminalCopy
        icon={isAccept
          ? <CheckCircle2 style={{ width: 32, height: 32, color: 'var(--positive)' }} />
          : <XCircle style={{ width: 32, height: 32, color: 'var(--ink-tertiary)' }} />}
        title={isAccept ? 'Assignment accepted' : 'Assignment declined'}
        body={isAccept
          ? `Thanks for accepting. ${state.workspaceName} has been notified.`
          : `Thanks for letting us know. ${state.workspaceName} has been notified.`}
        cta={isAccept ? <DownloadWorkOrderButton token={token} /> : undefined}
      />
    )
  }

  // ready or submitting — both show the job details + confirm UI
  const lookup = state.lookup
  const submitting = state.kind === 'submitting'
  const inlineError = state.kind === 'submitting' ? state.inlineError : ''
  return (
    <AssignmentForm
      lookup={lookup}
      action={action}
      submitting={submitting}
      inlineError={inlineError}
      declineReason={declineReason}
      setDeclineReason={setDeclineReason}
      onConfirm={handleConfirm}
      token={token}
    />
  )
}

function TerminalCopy({
  icon, title, body, cta,
}: { icon: React.ReactNode; title: string; body: string; cta?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '4px 0' }}>
      <div style={{ marginBottom: 14 }}>{icon}</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 10px', letterSpacing: '-0.02em' }}>{title}</h2>
      <p style={{ fontSize: 14, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.55 }}>{body}</p>
      {cta}
    </div>
  )
}

function AssignmentForm({
  lookup, action, submitting, inlineError, declineReason, setDeclineReason, onConfirm, token,
}: {
  lookup: LookupBody
  action: 'accept' | 'decline'
  submitting: boolean
  inlineError: string
  declineReason: string
  setDeclineReason: (s: string) => void
  onConfirm: () => void
  token: string
}) {
  const a = lookup.assignment
  const ws = a.workspace_name
  const isAccept = action === 'accept'
  const budgetNum = a.budget_offered == null ? 0 : Number(a.budget_offered)
  const showBudget = !Number.isNaN(budgetNum) && budgetNum > 0
  const address = composeAddress(a.job_address)
  const schedule = composeSchedule(a.job_scheduled_date, a.job_scheduled_start, a.job_scheduled_end)
  const hasReferences = !!(a.work_order_number || a.purchase_order_number)
  const hasNotes = !!(a.job_notes && a.job_notes.trim())
  const hasPhotos = a.photos.length > 0

  return (
    <>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
        Assignment from {ws}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 18px', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
        {a.job_title || 'Untitled job'}
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 10, marginBottom: hasReferences || hasNotes || hasPhotos ? 16 : 22 }}>
        {schedule && <DetailRow icon={<Calendar style={detailIcon} />} text={schedule} />}
        {address && <DetailRow icon={<MapPin style={detailIcon} />} text={address} />}
        {showBudget && <DetailRow icon={<DollarSign style={detailIcon} />} text={`Budget offered: $${budgetNum.toLocaleString('en-US')}`} />}
        {hasReferences && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: 'var(--ink-secondary)' }}>
            {a.work_order_number && <span><strong style={{ color: 'var(--ink-tertiary)', fontWeight: 600 }}>WO:</strong> {a.work_order_number}</span>}
            {a.purchase_order_number && <span><strong style={{ color: 'var(--ink-tertiary)', fontWeight: 600 }}>PO:</strong> {a.purchase_order_number}</span>}
          </div>
        )}
        {!schedule && !address && !showBudget && !hasReferences && (
          <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>No additional job details supplied.</span>
        )}
      </div>

      {hasNotes && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 6px' }}>Job details</p>
          {/* Preserve operator-authored line breaks; whiteSpace:pre-wrap also
              collapses excessive runs of internal whitespace which is fine
              for free-text intake. */}
          <p style={{ fontSize: 14, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {a.job_notes}
          </p>
        </div>
      )}

      {hasPhotos && (
        <div style={{ marginBottom: 22 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 8px' }}>Photos</p>
          {/* Thumbnails open in a new tab (target=_blank) rather than a
              lightbox. Rationale: this is a standalone vendor-facing token
              page with minimal chrome, and the page already follows a flat
              one-action style. A lightbox would introduce a focus-trap +
              keyboard nav surface that the rest of the page doesn't have;
              tab-open hands the photo to the OS-level image viewer (or a
              new browser tab) which is the lighter-weight interaction. The
              signed-URL TTL is 1 hour, plenty for an external open. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6 }}>
            {a.photos.map((p, i) => (
              <a key={i} href={p.signedUrl} target="_blank" rel="noopener noreferrer"
                aria-label={p.caption || `Photo ${i + 1}`}
                style={{ display: 'block', aspectRatio: '1 / 1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)' }}>
                <img src={p.signedUrl} alt={p.caption || ''} loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Download button — secondary to Accept/Decline. Visible on the
          pending (ready) state; the post-accept paths render their own
          copy in TerminalCopy below. */}
      <DownloadWorkOrderButton token={token} />

      {isAccept ? (
        <p style={{ fontSize: 14, color: 'var(--ink-secondary)', margin: '0 0 20px', lineHeight: 1.55 }}>
          Accepting will confirm to {ws} that you’re taking this job.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 14, color: 'var(--ink-secondary)', margin: '0 0 14px', lineHeight: 1.55 }}>
            Let {ws} know you’re declining. You can include a brief reason if you’d like.
          </p>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 7, letterSpacing: '0.02em' }}>
            Reason (optional)
          </label>
          <textarea
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            placeholder="e.g. booked that day, out of service area"
            rows={3}
            disabled={submitting}
            style={{
              width: '100%', padding: '11px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)',
              borderRadius: 10, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box',
              resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45, marginBottom: 18,
              transition: 'border-color 150ms',
            }}
            onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
            onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
          />
        </>
      )}

      {inlineError && (
        <p style={{ fontSize: 12, color: 'var(--danger)', margin: '0 0 12px' }}>{inlineError}</p>
      )}

      <button
        type="button"
        onClick={onConfirm}
        disabled={submitting}
        style={confirmButtonStyle(isAccept, submitting)}
      >
        {submitting ? (
          <>
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            {isAccept ? 'Accepting…' : 'Declining…'}
          </>
        ) : (
          isAccept ? 'Accept assignment' : 'Decline assignment'
        )}
      </button>
    </>
  )
}

function DetailRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      {icon}
      <span style={{ fontSize: 13.5, color: 'var(--ink-primary)', lineHeight: 1.5 }}>{text}</span>
    </div>
  )
}

// Token-gated PDF download. Same token surface as the lookup; the
// server returns a Content-Disposition with the filename baked in, so we
// parse it instead of guessing. POST keeps the token out of URL params
// for consistency with the other actions on the page; the new tab is
// avoided because Safari's download policy is friendlier with same-tab
// initiation.
function DownloadWorkOrderButton({ token }: { token: string }) {
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)

  const onClick = async () => {
    if (busy || !token) return
    setBusy(true); setErr(null)
    try {
      const resp = await fetch(WORK_ORDER_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ANON_KEY}`,
          'apikey': ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })
      if (!resp.ok) {
        let msg = `Download failed (HTTP ${resp.status})`
        try { msg = (await resp.json()).error ?? msg } catch { /* keep default */ }
        throw new Error(msg)
      }
      const disposition = resp.headers.get('content-disposition') ?? ''
      const m = disposition.match(/filename="?([^";]+)"?/)
      const filename = m ? m[1] : 'work-order.pdf'
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Defer revoke a tick so Safari's "Save As" UI has time to read.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
      <button type="button" onClick={onClick} disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          padding: '10px 14px', background: 'var(--hover-bg)',
          border: '1px solid var(--border-default)', borderRadius: 9,
          color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
        }}>
        {busy ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Download style={{ width: 14, height: 14 }} />}
        {busy ? 'Preparing PDF…' : 'Download work order (PDF)'}
      </button>
      {err && (
        <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>{err}</p>
      )}
    </div>
  )
}

// ─── Styling helpers ────────────────────────────────────────────────────────

const detailIcon: React.CSSProperties = {
  width: 15, height: 15, color: 'var(--ink-tertiary)', flexShrink: 0, marginTop: 2,
}

function confirmButtonStyle(isAccept: boolean, disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 18px',
    borderRadius: 10,
    border: isAccept ? '1px solid var(--positive-border)' : '1px solid var(--border-default)',
    background: isAccept ? 'var(--positive)' : 'var(--surface-raised)',
    color: isAccept ? 'var(--accent-ink)' : 'var(--ink-primary)',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '0.01em',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    transition: 'background 150ms, transform 80ms',
  }
}

// ─── Formatters ─────────────────────────────────────────────────────────────

function composeSchedule(date: string | null, start: string | null, end: string | null): string | null {
  if (!date && !start && !end) return null
  const parts: string[] = []
  if (date) parts.push(formatDate(date))
  if (start && end) parts.push(`from ${formatTime(start)} – ${formatTime(end)}`)
  else if (start) parts.push(`at ${formatTime(start)}`)
  else if (end) parts.push(`until ${formatTime(end)}`)
  return `Scheduled: ${parts.join(' ')}`
}

function composeAddress(a: LookupBody['assignment']['job_address']): string | null {
  const street = [a.line1, a.line2].filter(Boolean).join(', ')
  const region = [a.city, a.state].filter(Boolean).join(', ')
  const full = [street, region, a.zip].filter(Boolean).join(' • ')
  return full || null
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}

function formatTime(t: string): string {
  // scheduled_start may be ISO timestamp or HH:MM:SS — handle both.
  if (/^\d{2}:\d{2}/.test(t)) {
    const [h, m] = t.split(':')
    const hour = Number(h)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const h12 = ((hour + 11) % 12) + 1
    return `${h12}:${m} ${ampm}`
  }
  try {
    const d = new Date(t)
    if (Number.isNaN(d.getTime())) return t
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  } catch { return t }
}
